import { useMemo, useEffect } from 'preact/hooks';
import { shuffleArray, debounce } from '@/utils';
import { useWcagKeyboardEvents } from '@/features/conversational';
import {
	SvgIcon,
	useBlock,
	useFormStore,
	useProperties,
	useAdvanced
} from '@/features/form';
import Selectable from './Selectable';
import { getElementsHandleExtra } from '../utils';

export default function SelectList(
	{ block, state, setValue, setNestedValue, text, elementIndex, ...props },
	ref
) {
	const { currentConversationalFocusRef, actions, formType, lastKeyPressed, activePage } =
		useFormStore();
	const { conversational } = useProperties();
	const { lastPageAutoPost } = useAdvanced();
	const { properties } = block;
	const wrapperClass =
		['gcr', 'thumbs', 'category'].indexOf(block.typeName) > -1 ||
		properties.show_as_buttons
			? `button-container ${block.typeName}-container`
			: '';

	const currentPageBreak = useBlock({
		typeName: 'page_break',
		typeOf: activePage - 1
	});

	const withWcagKeyboardEvents =
		conversational ||
		(lastPageAutoPost && formType === 'embed') ||
		(currentPageBreak?.block?.properties?.autopost && formType === 'embed');

	const { onKeyDown, onChange, onClick, pendingScore, updatePendingScore } =
		useWcagKeyboardEvents({
			onArrowKeyPressed: e => {
				const updateHelperText = debounce(
					() => actions.updateScreenReaderHelper(text?.wcag?.ratingHelperText ?? ''),
					1000
				);
				updateHelperText();
				actions.updateLastKeyPressed(e.key);
			},
			onTabSpaceEnter: () => {
				if (pendingScore) {
					setValue(pendingScore);
					updatePendingScore(null);
					setTimeout(() => {
						actions.updateScreenReaderHelper('');
					}, 100);
				}
			},
			onChangedValue: e => {
				updatePendingScore(e.target.value);
				actions.updateLastKeyPressed(null);
			},
			onChangedValueConfirm: e => {
				actions.handleConversationalFocus(true);
				setValue(pendingScore);
				updatePendingScore(null);
			},
			onClickValue: (e, isMouseClick) => {
				updatePendingScore(
					!isMouseClick && block.typeName !== 'checkbox' ? e.target.value : null
				);
				setTimeout(() => {
					actions.updateScreenReaderHelper('');
				}, 100);
			}
		});

	const { elements, elementList } = useMemo(() => {
		const elements = getElementsHandleExtra({
			elements: properties.elements,
			elements_extra: properties.elements_extra
		});

		if (properties.reverseScore) {
			return {
				elements,
				elementList: Object.keys(elements).reverse()
			};
		}

		if (properties.randomize) {
			return {
				elements,
				elementList: shuffleArray(
					Object.keys(elements),
					Object.keys(properties.elements_extra ?? {}).length > 0
				)
			};
		}

		return {
			elements,
			elementList: Object.keys(elements)
		};
	}, [properties]);

	useEffect(() => {
		if (conversational && elementIndex !== 0) {
			actions.handleConversationalFocus();
			actions.updateLastKeyPressed(null);
		}
	}, []);

	return (
		<div
			class={wrapperClass}
			{...(conversational && {
				'aria-labelledby': `block-title-${block.typeName}-${block.id}`
			})}
			role="radiogroup"
			ref={currentConversationalFocusRef}
			{...(properties.required && { 'aria-required': true })}
			{...(conversational && {
				'aria-labelledby': `block-title-${block.typeName}-${block.id}`
			})}
		>
			{elementList.map((k, i) => {
				const el = elements[k];
				const value = el.hasOwnProperty('value') ? el.value : el.label;

				const attr = {
					block,
					value: value,
					key: i,
					selectableIndex: i,
					onChange: e => {
						if (withWcagKeyboardEvents && pendingScore) {
							onChange(e, { key: lastKeyPressed });
						} else {
							if (block.typeName === 'checkbox') {
								setNestedValue(el.data_field, e.target.checked);
							} else {
								setValue(e.target.value);
							}
						}
					},
					checked:
						block.typeName !== 'checkbox'
							? state.value === value || pendingScore === value
							: state[el.data_field]?.value && state[el.data_field]?.value !== 'false',
					onKeyDown: e => {
						if (withWcagKeyboardEvents) {
							onKeyDown(e);
						}
					},
					onClick: e => {
						if (withWcagKeyboardEvents) {
							onClick(e);
						}
					}
				};

				if (block.typeName === 'thumbs') {
					attr.labelAtts = {
						class: `label_thumbs_${i == 0 ? 'positive' : 'negative'}`,
						ariaLabel: `${el.label ? el.label : i === 0 ? 'positive' : 'negative'}`
					};
				}

				let thumbsProps =
					block.typeName === 'thumbs'
						? {
								style: { verticalAlign: 'middle' },
								alt: el.label
						  }
						: {};
				return (
					<Selectable {...attr}>
						{el.icon ? (
							<SvgIcon
								icon={el.icon}
								{...thumbsProps}
							/>
						) : null}
						{block.typeName !== 'thumbs' ? ' ' + el.label : null}
						{block.properties.showCaptions && Object.keys(thumbsProps).length ? (
							<span {...thumbsProps}>{' ' + el.label}</span>
						) : null}
					</Selectable>
				);
			})}
		</div>
	);
}
